import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { formatDate, isDate1BeforeOrEqual } from '../../../../helper/formatDate';
import {
    getWhatsAppAppUrl,
    getWhatsAppWebUrl,
    getServiceBadgeClass
} from '../EnquiryList/enquiryUtils';
import { FollowUpSchoolCell } from './FollowUpSchoolCell';
import { FollowUpMobileCard } from './FollowUpMobileCard';
import { FollowUpBtn } from '../FollowUpBtn';
import { FollowUpHistoryModal } from '../FollowUpHistoryModal';

export const getFollowUpColumns = ({ formattedDate, handleFollowSuccess, handleReport }) => [
    {
        Header: 'School Details',
        accessor: 'name',
        HeaderClass: 'text-start',
        DataClass: 'text-start align-middle',
        Cell: (cell) => <FollowUpSchoolCell row={cell.row.original} handleReport={handleReport} />
    },
    {
        Header: 'Phone / Principal',
        accessor: 'enquiry.phone',
        HeaderClass: 'text-start',
        DataClass: 'align-middle text-start',
        Cell: (cell) => {
            const row = cell.row.original;
            const enq = row.enquiry || {};
            return (
                <div>
                    {enq.father_name && (
                        <span
                            className="fw-semibold text-dark fs-13 d-block text-truncate"
                            title={`Principal: ${enq.father_name}`}
                        >
                            <i className="ri-user-star-line text-muted me-1" />
                            {enq.father_name}
                        </span>
                    )}
                    <a
                        href={`tel:${enq.phone}`}
                        className="text-success fw-medium text-decoration-none d-inline-flex align-items-center fs-13 mt-0.5"
                    >
                        <i className="bx bx-phone me-1 text-success fs-14" />
                        {enq.phone || 'N/A'}
                    </a>
                </div>
            );
        }
    },
    {
        Header: 'Date & Follow By',
        accessor: 'joining_date',
        HeaderClass: 'text-start',
        DataClass: 'align-middle text-start',
        Cell: (cell) => {
            const row = cell.row.original;
            const enq = row.enquiry || {};
            const user = enq.created_by;
            const followByName = user
                ? `${user.first_name} ${user.last_name || ''}`.trim()
                : (row.created_by ? `${row.created_by.first_name} ${row.created_by.last_name || ''}`.trim() : 'System');

            return (
                <div>
                    <span className="fw-bold text-dark fs-13 d-block">
                        {formatDate(row.joining_date)}
                    </span>
                    <span className="text-muted fs-12 d-inline-flex align-items-center">
                        <i className="ri-user-line me-1" />
                        {followByName}
                    </span>
                </div>
            );
        }
    },
    {
        Header: 'Preferred Service',
        accessor: 'enquiry.course.name',
        HeaderClass: 'text-center',
        DataClass: 'text-center align-middle',
        Cell: (cell) => {
            const row = cell.row.original;
            const serviceName = row.enquiry?.course?.name || cell.value;
            const badgeClass = getServiceBadgeClass(serviceName);
            return (
                <span className={`service-pill-badge ${badgeClass}`}>
                    {serviceName || 'N/A'}
                </span>
            );
        }
    },
    {
        Header: 'Action',
        HeaderClass: 'text-center',
        DataClass: 'text-center align-middle',
        Cell: (cell) => {
            const row = cell.row.original;
            const enq = row.enquiry || {};
            const appUrl = getWhatsAppAppUrl(enq.whatsapp, enq.phone, enq.name);
            const webUrl = getWhatsAppWebUrl(enq.whatsapp, enq.phone, enq.name);
            const hasPhone = Boolean(appUrl || webUrl);

            return (
                <div className="d-flex align-items-center justify-content-center gap-1">
                    {/* WhatsApp Action Button with App & Web choices */}
                    {hasPhone ? (
                        <Dropdown align="end" className="d-inline-block">
                            <Dropdown.Toggle
                                variant="soft-success"
                                size="sm"
                                className="py-1 px-2 border-0 arrow-none d-inline-flex align-items-center"
                                title="Chat on WhatsApp"
                            >
                                <i className="ri-whatsapp-line fs-14 text-success" />
                            </Dropdown.Toggle>

                            <Dropdown.Menu className="shadow border-0 py-1" style={{ minWidth: '185px', zIndex: 1050 }}>
                                <Dropdown.Item
                                    href={appUrl}
                                    className="d-flex align-items-center gap-2 py-1.5 fs-12 text-dark"
                                >
                                    <i className="ri-smartphone-line text-success fs-15" />
                                    <div>
                                        <span className="fw-semibold d-block">WhatsApp App</span>
                                        <small className="text-muted fs-11">Open Desktop / Phone App</small>
                                    </div>
                                </Dropdown.Item>

                                <Dropdown.Divider className="my-1" />

                                <Dropdown.Item
                                    href={webUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="d-flex align-items-center gap-2 py-1.5 fs-12 text-dark"
                                >
                                    <i className="ri-global-line text-primary fs-15" />
                                    <div>
                                        <span className="fw-semibold d-block">WhatsApp Web</span>
                                        <small className="text-muted fs-11">Open in Browser</small>
                                    </div>
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    ) : (
                        <button
                            type="button"
                            disabled
                            className="btn btn-sm btn-light py-1 px-2 opacity-50"
                            title="No WhatsApp/Phone number available"
                        >
                            <i className="ri-whatsapp-line fs-14" />
                        </button>
                    )}

                    {/* SMS / Follow-up History Modal */}
                    <FollowUpHistoryModal enquiryId={enq.id} schoolName={enq.name}>
                        <button className="btn btn-sm btn-soft-primary py-1 px-2" title="SMS / Follow-up History">
                            <i className="ri-chat-3-line fs-13" />
                        </button>
                    </FollowUpHistoryModal>

                    {/* Follow Up Action Button / Postponed */}
                    {isDate1BeforeOrEqual(formattedDate, row.joining_date) ? (
                        <FollowUpBtn follow={row} onSuccess={handleFollowSuccess} />
                    ) : (
                        <span className="btn btn-sm btn-soft-warning py-1 px-2 fs-11 align-middle">
                            PostPond
                        </span>
                    )}
                </div>
            );
        }
    },
    {
        Header: 'List',
        HeaderClass: 'd-none',
        DataClass: 'd-none',
        listClass: 'list-group-item border-0 px-0 bg-transparent',
        list: (row) => (
            <FollowUpMobileCard
                row={row}
                formattedDate={formattedDate}
                handleFollowSuccess={handleFollowSuccess}
                handleReport={handleReport}
            />
        )
    }
];

export default getFollowUpColumns;
